import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { allMovies, categories, siteInfo } from '@/mocks/movies';

export default function HomePage() {
  const [heroIndex, setHeroIndex] = useState(0);
  const heroMovies = allMovies.filter((m) => m.isHot).slice(0, 5);
  const hotMovies = allMovies.filter((m) => m.isHot);
  const newMovies = allMovies.filter((m) => m.isNew);
  const tvSeries = allMovies.filter((m) => m.categorySlug === 'tvseries');
  const allExceptTv = allMovies.filter((m) => m.categorySlug !== 'tvseries').slice(0, 8);

  const nextHero = useCallback(() => {
    setHeroIndex((prev) => (prev + 1) % heroMovies.length);
  }, [heroMovies.length]);

  useEffect(() => {
    const timer = setInterval(nextHero, 5000);
    return () => clearInterval(timer);
  }, [nextHero]);

  const currentHero = heroMovies[heroIndex];

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={currentHero.posterImage}
              alt={currentHero.title}
              className="w-full h-full object-cover object-top transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8 md:pb-12">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
                <div className="flex-1 max-w-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    {currentHero.isNew && (
                      <span className="px-2.5 py-0.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-xs font-semibold rounded-md">
                        热播中
                      </span>
                    )}
                    <span className="px-2.5 py-0.5 bg-accent-500 text-background-950 text-xs font-semibold rounded-md">
                      {currentHero.category}
                    </span>
                    <span className="text-foreground-300 text-xs">
                      <i className="ri-star-fill text-accent-500 mr-1"></i>
                      {currentHero.rating}
                    </span>
                  </div>
                  <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                    {currentHero.title}
                  </h1>
                  <p className="text-foreground-400 text-sm md:text-base line-clamp-2 mb-4">
                    {currentHero.description}
                  </p>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/article/${currentHero.id}`}
                      className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-md text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      立即观看
                    </Link>
                    <Link
                      to={`/article/${currentHero.id}`}
                      className="px-4 py-2.5 bg-background-100/80 hover:bg-background-200/80 backdrop-blur-sm text-foreground-950 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-information-line text-lg"></i>
                      详情
                    </Link>
                  </div>
                </div>

                <div className="hidden md:flex gap-2">
                  {heroMovies.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setHeroIndex(idx)}
                      className={`w-12 h-1 rounded-full transition-all duration-300 cursor-pointer ${
                        idx === heroIndex ? 'bg-primary-500 w-16' : 'bg-foreground-300/50 hover:bg-foreground-300'
                      }`}
                      aria-label={`切换到第${idx + 1}张`}
                    ></button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2">
            <button
              onClick={() => setHeroIndex((prev) => (prev - 1 + heroMovies.length) % heroMovies.length)}
              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100/40 hover:bg-background-100/70 backdrop-blur-sm transition-all cursor-pointer"
              aria-label="上一个"
            >
              <i className="ri-arrow-up-s-line text-foreground-950"></i>
            </button>
            <button
              onClick={() => setHeroIndex((prev) => (prev + 1) % heroMovies.length)}
              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100/40 hover:bg-background-100/70 backdrop-blur-sm transition-all cursor-pointer"
              aria-label="下一个"
            >
              <i className="ri-arrow-down-s-line text-foreground-950"></i>
            </button>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-all duration-200 group cursor-pointer"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-primary-500/10 group-hover:bg-primary-500/20 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl text-primary-500`}></i>
                  </div>
                  <span className="text-xs text-foreground-500 group-hover:text-foreground-950 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  热门推荐
                </h2>
              </div>
              <Link to="/category/all" className="text-sm text-foreground-400 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} variant="poster" />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新更新
                </h2>
              </div>
              <Link to="/category/all" className="text-sm text-foreground-400 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} variant="poster" />
              ))}
            </div>
          </div>
        </section>

        {/* TV Series */}
        <section className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  电视剧大全
                </h2>
              </div>
              <Link to="/category/tvseries" className="text-sm text-foreground-400 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {tvSeries.map((movie) => (
                <MovieCard key={movie.id} movie={movie} variant="landscape" />
              ))}
            </div>
          </div>
        </section>

        {/* All Movies Grid */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  精选好片
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {allExceptTv.map((movie) => (
                <MovieCard key={`all-${movie.id}`} movie={movie} variant="poster" />
              ))}
            </div>
          </div>
        </section>

        {/* Stats & CTA */}
        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto">
            <div className="rounded-xl bg-background-100 p-8 md:p-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {siteInfo.stats.totalMovies}
                  </div>
                  <p className="text-foreground-400 text-sm">影视资源</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {siteInfo.stats.dailyVisitors}
                  </div>
                  <p className="text-foreground-400 text-sm">日访问量</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {siteInfo.stats.updateFrequency}
                  </div>
                  <p className="text-foreground-400 text-sm">更新频率</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {siteInfo.stats.years}
                  </div>
                  <p className="text-foreground-400 text-sm">稳定运营</p>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-md text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-movie-line text-lg"></i>
                  立即开始观影
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
              <p className="text-foreground-400 text-sm">关于7777影院的常见疑问解答</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { q: '7777影院是否需要注册？', a: '无需注册即可免费观看所有影片，方便快捷。' },
                { q: '影片画质如何？', a: '我们提供高清和超清两种画质选择，根据您的网络环境自动适配最佳画质。' },
                { q: '更新速度怎么样？', a: '每日更新最新电影和电视剧资源，热门影片上线后24小时内即可观看。' },
                { q: '支持哪些设备观看？', a: '支持电脑、平板、手机等全平台观看，随时随地享受观影乐趣。' },
                { q: '是否需要安装播放器？', a: '无需安装任何播放器，浏览器直接播放，即点即看。' },
                { q: '遇到播放问题怎么办？', a: '可联系在线客服或发送邮件至客服邮箱，我们会在第一时间为您解决。' },
              ].map((faq, idx) => (
                <div key={idx} className="p-4 rounded-lg bg-background-50 border border-background-200/60">
                  <h4 className="text-foreground-950 font-medium text-sm mb-2">
                    <a href="#" className="cursor-default">{faq.q}</a>
                  </h4>
                  <p className="text-foreground-400 text-xs leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '7777影院',
            alternateName: '77777777影院',
            url: 'https://7777cinema.com/',
            description: siteInfo.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://7777cinema.com/category/all?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '7777影院',
            alternateName: '77777777影院',
            url: 'https://7777cinema.com/',
            email: siteInfo.email,
            telephone: siteInfo.phone,
            address: {
              '@type': 'PostalAddress',
              addressLocality: '北京',
              addressRegion: '北京',
              addressCountry: 'CN',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '7777影院是否需要注册？', acceptedAnswer: { '@type': 'Answer', text: '无需注册即可免费观看所有影片，方便快捷。' } },
              { '@type': 'Question', name: '影片画质如何？', acceptedAnswer: { '@type': 'Answer', text: '我们提供高清和超清两种画质选择，根据您的网络环境自动适配最佳画质。' } },
              { '@type': 'Question', name: '更新速度怎么样？', acceptedAnswer: { '@type': 'Answer', text: '每日更新最新电影和电视剧资源，热门影片上线后24小时内即可观看。' } },
              { '@type': 'Question', name: '支持哪些设备观看？', acceptedAnswer: { '@type': 'Answer', text: '支持电脑、平板、手机等全平台观看，随时随地享受观影乐趣。' } },
              { '@type': 'Question', name: '是否需要安装播放器？', acceptedAnswer: { '@type': 'Answer', text: '无需安装任何播放器，浏览器直接播放，即点即看。' } },
              { '@type': 'Question', name: '遇到播放问题怎么办？', acceptedAnswer: { '@type': 'Answer', text: '可联系在线客服或发送邮件至客服邮箱，我们会在第一时间为您解决。' } },
            ],
          }),
        }}
      />

      <Footer />
    </>
  );
}